import { intl } from '@beelday/common'
import { useAuthenticatedBeeldayClient } from 'connectivity/beelday-hooks'
import { useClicker } from 'features/script/use-clicker'
import { useCallback } from 'react'
import { MenuItem } from 'room/common/presentation/menu'
import { LobbyMoment } from 'room/lobby/lobby-moment'
import { useLobbyMoment } from 'room/lobby/use-lobby-moment'
import { EndBreak } from './icon/end-break'
import { StartIcon } from './icon/start-icon'
import { useAssertedJoinedRoomAddress } from '../use-joined-room-address'

type Props = {
	/**
	 * `undefined` while it is being read. The action stays hidden until then:
	 * "Start" pressed during a break sends the training back to its initial
	 * phase.
	 */
	moment: LobbyMoment | undefined
	/**
	 * The trainer's presentation clicker ends a break too: Next does what the
	 * button does. Not before the start - a stray press while the room fills
	 * up must not start the training.
	 */
	clicker?: boolean
}

/**
 * What Start and End break do: end the break, or start the training. One
 * request for every button that offers it - the menu's, and the in-person
 * lobby's own - so they cannot decide differently.
 */
export const useResumeTraining = (
	moment: LobbyMoment | undefined
): (() => Promise<unknown>) => {
	const beeldayClient = useAuthenticatedBeeldayClient()
	const address = useAssertedJoinedRoomAddress()

	return useCallback(
		(): Promise<unknown> =>
			moment === 'break'
				? beeldayClient.endBreak(address)
				: beeldayClient.startTraining(address),
		[address, beeldayClient, moment]
	)
}

/**
 * For a screen that has read the lobby moment already: the in-person lobby
 * shows it on its card, and the action must not decide differently.
 */
export const ResumeTrainingAction = ({
	moment,
	clicker = false,
}: Props): JSX.Element => {
	const resumeTraining = useResumeTraining(moment)

	useClicker({
		enabled: clicker && moment === 'break',
		stepKey: moment ?? '',
		act: command => (command === 'next' ? resumeTraining() : undefined),
	})

	return moment === 'break' ? (
		<MenuItem
			icon={<EndBreak />}
			onClick={resumeTraining}
			label={<intl.Translate>lobby.end_break</intl.Translate>}
		/>
	) : (
		<MenuItem
			icon={<StartIcon />}
			onClick={resumeTraining}
			hidden={!moment}
			label={<intl.Translate>lobby.start_training</intl.Translate>}
		/>
	)
}

const ResumeTrainingMenuItem = (): JSX.Element => (
	<ResumeTrainingAction moment={useLobbyMoment()} />
)

export default ResumeTrainingMenuItem
